import { motion } from "framer-motion";
import { cn } from "../../lib/cn";

interface ProgressRingProps {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
  fillColor?: string;
  className?: string;
  children?: React.ReactNode;
}

function ProgressRing({
  value,
  max = 100,
  size = 64,
  strokeWidth = 4,
  trackColor = "var(--border-hairline)",
  fillColor = "var(--color-lever-orange)",
  className,
  children,
}: ProgressRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(1, value / max));
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div
      className={cn("relative inline-flex items-center justify-center", className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={fillColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{
            type: "spring",
            stiffness: 60,
            damping: 15,
            duration: 0.8,
          }}
        />
      </svg>
      {children && (
        <div className="relative z-10 flex items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );
}

ProgressRing.displayName = "ProgressRing";

export { ProgressRing };
export type { ProgressRingProps };
